import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { listAccounts } from '@/api/accounts';
import { useAuth } from '@/auth/useAuth';
import { accountSubtypes } from './schema';
import { formatDateTime } from '@/lib/dateFormat';
import type { AccountSource, AccountSubtype } from '@/types/oscar';
import { useSort } from '@/lib/sortTable';
import { SortHeader } from '@/components/ui/SortHeader';
import { DesktopTable, MobileCards, MobileCard, CardField } from '@/components/common/responsiveList';

const PAGE_SIZE = 50;

const subtypeBadge: Record<AccountSubtype, string> = {
  ServiceAccount: 'bg-lightBlue/20 text-navy',
  SharedMailbox: 'bg-green/15 text-green',
  SharedSignIn: 'bg-orange/15 text-orange',
  AppRegistration: 'bg-navy/10 text-navy',
  Other: 'bg-darkGrey/10 text-darkGrey',
};

const sourceBadge: Record<AccountSource, string> = {
  EntraCloud: 'bg-lightBlue/20 text-navy',
  OnPremAD: 'bg-darkGrey/10 text-darkGrey',
  Manual: 'bg-green/15 text-green',
  Other: 'bg-darkGrey/10 text-darkGrey',
};

function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export function AccountsRegister() {
  const { canMutate } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [subtype, setSubtype] = useState('');
  const [source, setSource] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  // Honour ?needsTriage=1 deep links (from the dashboard widget).
  const [needsTriage, setNeedsTriage] = useState(searchParams.get('needsTriage') === '1');
  const search = useDebounced(searchInput);
  const { sortKey, sortDir, onSort } = useSort('name');
  const handleSort = (k: string) => { onSort(k); setPage(1); };

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (needsTriage) next.set('needsTriage', '1'); else next.delete('needsTriage');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsTriage]);

  const query = useQuery({
    queryKey: ['accounts', { subtype, source, search, needsTriage, sortKey, sortDir, page }],
    queryFn: ({ signal }) =>
      listAccounts({ subtype, source, search, needsTriage, sort: sortKey, dir: sortDir, page, pageSize: PAGE_SIZE }, signal),
    placeholderData: keepPreviousData,
  });

  const total = query.data?.Total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section>
      <header className="mb-5 flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-navy">Azure Apps</h1>
          <p className="mt-1 text-sm text-darkGrey/80">Entra application registrations and service principals.</p>
        </div>
        {canMutate && (
          <Link
            to="/accounts/new"
            className="inline-flex items-center gap-2 rounded bg-orange px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            <KeyRound className="h-4 w-4" aria-hidden /> Add account
          </Link>
        )}
      </header>

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Subtype
          <select
            aria-label="Filter by subtype"
            value={subtype}
            onChange={(e) => { setSubtype(e.target.value); setPage(1); }}
            className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal"
          >
            <option value="">All</option>
            {accountSubtypes.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Source
          <select
            aria-label="Filter by source"
            value={source}
            onChange={(e) => { setSource(e.target.value); setPage(1); }}
            className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal"
          >
            <option value="">All</option>
            {(['EntraCloud', 'OnPremAD', 'Manual', 'Other'] as const).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="flex flex-1 flex-col text-xs font-semibold text-darkGrey">
          Search
          <input
            aria-label="Search accounts"
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
            placeholder="Display name or UPN"
            className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal"
          />
        </label>
        <label className="flex items-center gap-2 pb-1.5 text-xs font-semibold text-darkGrey">
          <input
            type="checkbox"
            aria-label="Needs triage"
            checked={needsTriage}
            onChange={(e) => { setNeedsTriage(e.target.checked); setPage(1); }}
          />
          Needs triage
        </label>
      </div>

      <DesktopTable className="overflow-x-auto rounded border border-border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-bg text-left text-xs uppercase tracking-wide text-darkGrey/70">
            <tr>
              <SortHeader label="Display name" columnKey="name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Subtype" columnKey="subtype" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Source" columnKey="source" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <th className="px-3 py-2">Owner</th>
              <SortHeader label="UPN" columnKey="upn" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Last sign-in" columnKey="lastSignIn" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
            </tr>
          </thead>
          <tbody>
            {query.isLoading && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-darkGrey/60">Loading…</td></tr>
            )}
            {query.isError && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-red">Could not load accounts.</td></tr>
            )}
            {query.data?.Items.length === 0 && !query.isLoading && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-darkGrey/60">No accounts match the filters.</td></tr>
            )}
            {query.data?.Items.map((a) => (
              <tr key={a.SystemAccountId} className="border-t border-border hover:bg-bg/60">
                <td className="px-3 py-2">
                  <Link to={`/accounts/${a.SystemAccountId}`} className="font-medium text-navy hover:underline">
                    {a.DisplayName}
                  </Link>
                </td>
                <td className="px-3 py-2">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${subtypeBadge[a.Subtype]}`}>{a.Subtype}</span>
                </td>
                <td className="px-3 py-2">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${sourceBadge[a.Source]}`}>{a.Source}</span>
                </td>
                <td className="px-3 py-2 text-darkGrey/80">{a.OwnerPersonName ?? (a.OwnerPersonId ? `#${a.OwnerPersonId}` : '—')}</td>
                <td className="px-3 py-2 text-darkGrey/80">{a.UPN ?? '—'}</td>
                <td className="px-3 py-2 text-darkGrey/80">{a.LastSignIn ? formatDateTime(a.LastSignIn) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DesktopTable>

      {/* Mobile (<md): accounts as stacked cards. */}
      <MobileCards>
        {query.isLoading && <p className="px-1 py-6 text-center text-sm text-darkGrey/60">Loading…</p>}
        {query.isError && <p className="px-1 py-6 text-center text-sm text-red">Could not load accounts.</p>}
        {query.data?.Items.length === 0 && !query.isLoading && (
          <p className="px-1 py-6 text-center text-sm text-darkGrey/60">No accounts match the filters.</p>
        )}
        {query.data?.Items.map((a) => (
          <MobileCard key={a.SystemAccountId}>
            <div className="flex items-start justify-between gap-2">
              <Link to={`/accounts/${a.SystemAccountId}`} className="text-sm font-semibold text-navy hover:text-orange hover:underline">
                {a.DisplayName}
              </Link>
              <span className="flex shrink-0 flex-wrap items-center justify-end gap-1">
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${subtypeBadge[a.Subtype]}`}>{a.Subtype}</span>
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${sourceBadge[a.Source]}`}>{a.Source}</span>
              </span>
            </div>
            <div className="mt-2 border-t border-border pt-2">
              <CardField label="Owner">{a.OwnerPersonName ?? (a.OwnerPersonId ? `#${a.OwnerPersonId}` : '—')}</CardField>
              <CardField label="UPN"><span className="break-all">{a.UPN ?? '—'}</span></CardField>
              <CardField label="Last sign-in">{a.LastSignIn ? formatDateTime(a.LastSignIn) : '—'}</CardField>
            </div>
          </MobileCard>
        ))}
      </MobileCards>

      <div className="mt-3 flex items-center justify-between text-sm text-darkGrey/80">
        <span>{total} {total === 1 ? 'account' : 'accounts'}</span>
        <div className="flex items-center gap-2">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded border border-border bg-white px-2 py-1 disabled:opacity-40">Previous</button>
          <span>Page {page} of {totalPages}</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="rounded border border-border bg-white px-2 py-1 disabled:opacity-40">Next</button>
        </div>
      </div>
    </section>
  );
}
